import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { alpha, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { teamRosterCell, type TeamRosterRow } from "_shared/entity-members/_views/team-roster-table";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import VendorTeamDeparture, {
  VendorTeamDepartureReason,
} from "work/work-agreements/teams/vendor-team/vendor-team-departure";

/**
 * What a departed row says when the directory will not name the person on it. The same words the
 * roster uses for an unresolved member, because a GUID is not something to show a reader (#4830).
 */
const UNNAMED_FORMER_MEMBER = "Vendor team member";

/**
 * The colour of the line struck through a departed row, and the one place the two reasons look
 * different (#5834). Gray for somebody who took themselves off; a muted red for somebody a leader took
 * off. Deliberately slight: the Role cell already says which of the two it was in words, so the line
 * only has to tell them apart at a glance, never on its own.
 */
function departedRowStrikeColour(theme: Theme, reason: VendorTeamDepartureReason): string {
  return reason === VendorTeamDepartureReason.Removed
    ? alpha(theme.palette.error.main, 0.6)
    : theme.palette.text.disabled;
}

/**
 * The whole row of somebody who is no longer on the team, grayed and struck through — every cell, and
 * the avatar with it (#5834). Striking only the name left the avatar, email and company reading as a
 * current member's.
 *
 * Drawn on the cells rather than the `tr`, because a table paints its text there. One declaration on
 * each cell is enough: a line-through carries into everything the cell holds, and it takes its colour
 * from the cell that declared it. The Typography inside a cell sets its own colour, so it is told to
 * inherit the cell's gray instead. `text.secondary` rather than `text.disabled`: this is information a
 * reader still has to read, and the disabled gray falls below a readable contrast on the paper.
 */
function departedMemberRowStyles(
  reason: VendorTeamDepartureReason,
): (theme: Theme) => SystemStyleObject<Theme> {
  return (theme) => ({
    "& > td": {
      color: theme.palette.text.secondary,
      textDecorationLine: "line-through",
      textDecorationColor: departedRowStrikeColour(theme, reason),
    },
    "& > td .MuiTypography-root": {
      color: "inherit",
    },
    "& [data-roster-avatar-slot] .MuiAvatar-root": {
      filter: "grayscale(1)",
      opacity: 0.6,
    },
  });
}

/**
 * The rows a work agreement's roster draws for the people who have left its vendor team, oldest
 * departure first.
 *
 * Each row's Role cell carries the status rather than a role: the person is no longer anything to the
 * team, and which of the two ways they left is the whole point of the row (#5663). Before this, a
 * member who took themselves off and a member a leader took off both simply stopped appearing, so
 * neither event was readable on the team at all.
 *
 * `data-departed-member` carries the reason's own name, so a browser test can tell the two apart
 * without matching the words a screen shows.
 *
 * A continuation hired after a restricted leave starts with no departures of its own, so the people
 * who left the agreement it replaced are not listed on it. That is intended (#5834): they left a team the
 * new agreement never had, and each earlier agreement still lists the people who left it.
 */
export default function vendorTeamDepartureRows(args: {
  departures: readonly VendorTeamDeparture[];
  individuals: readonly Individual[];
}): TeamRosterRow[] {
  const { departures, individuals } = args;
  if (departures.length === 0) return [];

  const individualsByUserId = new Map(individuals.map((individual) => [individual.userId.value, individual]));

  return [...departures]
    .sort((left, right) => left.departedDate.valueOf() - right.departedDate.valueOf())
    .map((departure) => {
      const individual = individualsByUserId.get(departure.userId.value);
      const displayName = individual?.fullName ?? UNNAMED_FORMER_MEMBER;
      return {
        id: `departed:${departure.userId.value}:${departure.legalEntityId.value}`,
        searchText: `${displayName} ${departure.status}`.trim(),
        sortText: displayName,
        avatar: individual ? <IndividualAvatar individual={individual} /> : <Avatar />,
        name: <Typography variant="body2">{displayName}</Typography>,
        detail: departure.departedDate.format("MMM D, YYYY"),
        email: teamRosterCell(individual?.email?.value),
        company: teamRosterCell(individual?.entityName),
        role: { content: departure.status, searchText: departure.status },
        rowAttributes: { "data-departed-member": departure.reason },
        rowSx: departedMemberRowStyles(departure.reason),
      };
    });
}
